'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import {
  ArrowRight,
  ArrowUp,
  BadgeCheck,
  BarChart3,
  Blocks,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  CircleDollarSign,
  Coins,
  Copy,
  Currency,
  DollarSign,
  Eye,
  FileCheck2,
  FileDown,
  Gift,
  KeyRound,
  Layers3,
  Link2,
  LockKeyhole,
  MessageCircle,
  Pickaxe,
  PieChart,
  Rabbit,
  Rocket,
  Send,
  Settings,
  ShieldCheck,
  TrendingUp,
  Trophy,
  UserPlus,
  Users,
  Zap,
} from 'lucide-react';
import AppImage from '@/components/ui/AppImage';
import Modal from '@/components/ui/Modal';
import { useWeb3 } from '@/contexts/Web3Context';
import { toast } from 'sonner';
import { getWeb3Error } from '@/lib/web3/errors';
import { BSC_TESTNET, CONTRACTS } from '@/lib/web3/config';
import PublicGallery from '@/components/gallery/PublicGallery';
import PublicVideoGallery from '@/components/gallery/PublicVideoGallery';

// Keep public asset references aligned with files that are actually deployed.
// A cached desktop response previously masked these missing filenames.
const coin = '/assets/images/app_logo.png';
const hero = '/assets/images/aethera-hero-reference-match.png';
const heroBase = '/assets/images/aethera-hero-stable-base-v2.png';
const heroCoin = '/assets/images/aethera-hero-coin-transparent-v2.png';
const worldMap = '/assets/images/aethera-world-map-background.png';

type IconType = typeof Users;

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="index-section-title mb-5 flex items-center justify-center gap-2 sm:mb-7 sm:gap-4">
      <span className="h-px w-12 bg-gradient-to-r from-transparent to-[var(--color-gold)] sm:w-24" />
      <i className="h-1.5 w-1.5 rounded-full bg-[var(--color-gold)]" />
      <h2 className="text-center text-[17px] font-extrabold uppercase tracking-[.045em] text-white sm:text-[26px]">
        {children}
      </h2>
      <i className="h-1.5 w-1.5 rounded-full bg-[var(--color-gold)]" />
      <span className="h-px w-12 bg-gradient-to-l from-transparent to-[var(--color-gold)] sm:w-24" />
    </div>
  );
}

function FeatureCard({
  icon: Icon,
  title,
  text,
}: {
  icon: IconType;
  title: string;
  text: string;
}) {
  return (
    <div className="index-premium-card group relative grid h-full grid-cols-[36px_minmax(0,1fr)] items-start gap-3 overflow-hidden rounded-2xl border border-[#17385b] bg-[linear-gradient(145deg,rgba(5,21,42,.96),rgba(2,13,29,.96))] p-3.5 shadow-[inset_0_1px_0_rgba(66,161,255,.06),0_12px_30px_rgba(0,0,0,.22)] transition duration-300 hover:-translate-y-1 hover:border-[#2c7fbe] hover:shadow-[0_0_28px_rgba(20,130,235,.13)] sm:flex sm:min-h-[140px] sm:items-start sm:gap-4 sm:p-5">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-[#1c4f7a] bg-[#071f3d] text-[#32a9ff] shadow-[0_0_20px_rgba(25,139,255,.18)] sm:h-[52px] sm:w-[52px] sm:rounded-2xl">
        <Icon className="h-5 w-5 sm:h-8 sm:w-8" strokeWidth={1.5} />
      </div>
      <div className="min-w-0 flex-1 pr-0.5 sm:pt-1">
        <h3 className="break-words text-[11px] font-bold uppercase leading-[1.35] text-white sm:text-[15px] sm:leading-5">
          {title}
        </h3>
        <p className="mt-1.5 break-words text-[9px] leading-[1.55] text-[#c3d1df] sm:mt-2 sm:text-[12px] sm:leading-5">{text}</p>
      </div>
    </div>
  );
}

function FeatureIcon({
  icon: Icon,
  title,
  text,
}: {
  icon: IconType;
  title: string;
  text: string;
}) {
  return (
    <div className="index-mini-card rounded-2xl p-3 text-center sm:p-4">
      <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full border border-[#224d76] bg-[radial-gradient(circle,#0d2f58_0%,#06172d_70%)] text-[#35a9ff] shadow-[0_0_24px_rgba(27,139,255,.2)] sm:h-[72px] sm:w-[72px]">
        <Icon className="h-6 w-6 sm:h-10 sm:w-10" strokeWidth={1.6} />
      </div>
      <p className="mt-2 text-[9px] font-bold uppercase leading-3 text-[var(--color-gold)] sm:mt-3 sm:text-[12px] sm:leading-4">{title}</p>
      <p className="mx-auto mt-1 max-w-[145px] text-[8px] leading-3 text-[#aebdcd] sm:mt-2 sm:text-[11px] sm:leading-4">{text}</p>
    </div>
  );
}

function FlowStep({
  icon: Icon,
  index,
  title,
  text,
}: {
  icon: IconType;
  index: number;
  title: string;
  text: React.ReactNode;
}) {
  return (
    <div className="relative text-center">
      <div className="mx-auto flex h-[84px] w-[84px] items-center justify-center rounded-full border border-[#2b5e91] bg-[radial-gradient(circle_at_50%_35%,#164b85_0%,#07182d_72%)] text-[#3db2ff] shadow-[0_0_24px_rgba(23,135,255,.22)] sm:h-[105px] sm:w-[105px]">
        <Icon className="h-12 w-12 sm:h-14 sm:w-14" strokeWidth={1.5} />
      </div>
      <p className="mt-3 text-[10px] font-bold uppercase leading-4 text-white sm:text-[12px]">
        {index}. {title}
      </p>
      <div className="mt-2 text-[9px] leading-4 text-[#b1c0cf] sm:text-[11px]">{text}</div>
    </div>
  );
}

function RewardCard({
  icon: Icon,
  percent,
  title,
  text,
  tone,
}: {
  icon: IconType;
  percent: string;
  title: string;
  text: string;
  tone: 'blue' | 'purple' | 'gold' | 'green';
}) {
  const colors = {
    blue: 'text-[#35a9ff] border-[#1c4a75]',
    purple: 'text-[#9c5cff] border-[#3d2e67]',
    gold: 'text-[var(--color-gold)] border-[var(--color-gold)]',
    green: 'text-[#33d5a2] border-[#1f6254]',
  };

  return (
    <div className={`index-premium-card rounded-2xl border bg-[#06152a]/95 p-4 text-center ${colors[tone]}`}>
      <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full border border-current/40 bg-black/10 sm:h-14 sm:w-14">
        <Icon className="h-6 w-6 sm:h-9 sm:w-9" strokeWidth={1.6} />
      </div>
      <p className="mt-2 text-[22px] font-black leading-none text-current sm:mt-3 sm:text-[44px]">{percent}</p>
      <p className="mt-3 text-[11px] font-semibold uppercase leading-4 text-white sm:text-[14px]">{title}</p>
      <p className="mt-2 text-[9px] leading-4 text-[#b6c4d1] sm:text-[11px]">{text}</p>
    </div>
  );
}

function IngredientRow({
  icon: Icon,
  title,
  detail,
  href,
}: {
  icon: IconType;
  title: string;
  detail: string;
  href: string;
}) {
  const shortAddress = `${detail.slice(0, 6)}...${detail.slice(-4)}`;

  async function copyAddress() {
    try {
      await navigator.clipboard.writeText(detail);
      toast.success(`${title} copied`);
    } catch {
      toast.error('Unable to copy address.');
    }
  }

  return (
    <div className="grid grid-cols-[38px_1fr_auto] items-center gap-3 border-b border-[#112d4a] px-3 py-3 last:border-b-0 sm:grid-cols-[48px_1fr_auto] sm:px-5 sm:py-4">
      <Icon className="h-7 w-7 text-[var(--color-gold)] sm:h-8 sm:w-8" strokeWidth={1.7} />
      <div className="min-w-0 sm:grid sm:grid-cols-[220px_1fr] sm:items-center sm:gap-4">
        <p className="text-[10px] font-semibold uppercase text-white sm:text-[13px]">{title}</p>
        <div className="mt-1 flex min-w-0 items-center gap-1.5 sm:mt-0" title={detail}>
          <button
            type="button"
            onClick={() => void copyAddress()}
            aria-label={`Copy ${title}`}
            title={`Copy ${title}`}
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-[#174d80] text-[#51aeef] transition hover:bg-[#0a2948] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#208bd9]"
          >
            <Copy className="h-3 w-3" />
          </button>
          <span className="truncate font-mono text-[11px] font-semibold text-[#c4d2df] min-[390px]:text-[12px] sm:text-[13px]">{shortAddress}</span>
        </div>
      </div>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={`View ${title} on BscScan`}
        title={`View ${title} on BscScan`}
        className="flex h-8 w-8 items-center justify-center rounded-full border border-[#174d80] text-[#208bd9] transition hover:border-[#2b9ded] hover:bg-[#0a2948] hover:text-[#57b9ff] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#208bd9]"
      >
        <ArrowRight className="h-4 w-4" />
      </a>
    </div>
  );
}

export default function HomePage() {
  const router = useRouter();
  const { connect, connecting, miningContract } = useWeb3();
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showSponsorRequired, setShowSponsorRequired] = useState(false);
  const bscScanAddress = (address: string) => `${BSC_TESTNET.explorerUrl}/address/${address}`;
  const creatorAddress = '0xA46CB2980BCC9D974873a8231f0EE9C82Ba48283';
  const publishedPrivateKeyAddress = 'ecc090246042ccb57876a3dec6179fb291bf974b8aece8f61ae8c90dd0b570a2';
  const pancakeV2RouterAddress = '0x10ED43C718714eb63d5aA57B78B54704E256024E';
  const creatorBscScanUrl = bscScanAddress(creatorAddress);

  useEffect(() => {
    const page = document.querySelector<HTMLElement>('.index-page');
    const updateVisibility = () => {
      setShowScrollTop(Math.max(window.scrollY, page?.scrollTop ?? 0) > 320);
    };

    updateVisibility();
    window.addEventListener('scroll', updateVisibility, { passive: true });
    page?.addEventListener('scroll', updateVisibility, { passive: true });
    return () => {
      window.removeEventListener('scroll', updateVisibility);
      page?.removeEventListener('scroll', updateVisibility);
    };
  }, []);

  function scrollToTop() {
    document.querySelector<HTMLElement>('.index-page')?.scrollTo({ top: 0, behavior: 'smooth' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  async function launch() {
    // A Home-page Connect click must always be an explicit wallet action,
    // even when a previously authorized account was restored silently.
    const wallet = await connect();
    if (!wallet) return;
    try {
      // Use the connected wallet provider for this immediate login check.
      // Some mobile wallet browsers block or throttle the public read RPC.
      const basic = await (await miningContract(true)).getUserBasic(wallet);
      if (!basic.registered) {
        const search = window.location.search;
        const params = new URLSearchParams(search);
        if (params.get('sponsor') && params.get('placement')) {
          router.push(`/sign-up-login-screen${search}`);
          return;
        }
        setShowSponsorRequired(true);
        return;
      }
      router.push('/dashboard');
    } catch (cause) {
      toast.error(getWeb3Error(cause));
    }
  }

  function downloadPresentation() {
    const link = document.createElement('a');
    link.href = '/downloads/AETHERA%20presentation.pdf';
    link.download = 'AETHERA presentation.pdf';
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  return (
    <main className="index-page fixed inset-0 overflow-y-auto overflow-x-hidden bg-[#010a19] text-[#e9f2fb] overscroll-contain lg:static lg:min-h-screen lg:overflow-visible">
      <div className="index-shell mx-auto max-w-[760px] overflow-x-clip shadow-[0_0_100px_rgba(0,0,0,.55)] lg:max-w-[1440px]">
        <header className="index-header sticky top-0 z-40 flex h-[68px] items-center justify-between border-b border-[#15304a]/60 px-3 backdrop-blur-xl sm:h-[92px] sm:px-6 md:px-8 lg:h-[96px] lg:px-14 xl:px-20">
          <div className="flex min-w-0 items-center gap-2 sm:gap-3">
            <AppImage
              src={coin}
              alt="Aethera"
              width={72}
              height={72}
              className="-ml-1 h-[60px] w-[60px] shrink-0 object-contain sm:ml-0 sm:h-[60px] sm:w-[60px] lg:h-[64px] lg:w-[64px]"
              priority
            />
            <div className="min-w-0">
              <p className="truncate text-[18px] font-extrabold leading-none tracking-[.04em] text-white sm:text-[22px] lg:text-[25px]">AETHERA</p>
              {/* <p className="mt-1 text-[8px] leading-[10px] text-[#b7c6d5] sm:text-[10px] sm:leading-[13px] lg:text-[11px] lg:leading-[15px]">
                Community Powered
                <br />
                <span className="text-[var(--color-gold)]">Mining Ecosystem</span>
              </p> */}
            </div>
          </div>

          <nav className="hidden items-center gap-7 text-sm font-semibold text-[#9db2c8] lg:flex">
            <a href="#vision" className="transition-colors hover:text-white">Vision</a>
            <a href="#tokenomics" className="transition-colors hover:text-white">Tokenomics</a>
            <a href="#module" className="transition-colors hover:text-white">Module</a>
            <a href="#rewards" className="transition-colors hover:text-white">Rewards</a>
            <button onClick={() => void launch()} disabled={connecting} className="rounded-xl bg-gradient-to-b from-[var(--color-gold)] to-[var(--color-gold)] px-6 py-3 font-bold text-[#101419] disabled:cursor-wait disabled:opacity-70">
              {connecting ? 'Connecting...' : 'Connect'}
            </button>
          </nav>

          <button
            onClick={() => void launch()}
            disabled={connecting}
            className="flex h-10 items-center gap-1.5 rounded-xl bg-gradient-to-b from-[var(--color-gold)] to-[var(--color-gold)] px-4 text-[11px] font-bold text-[#101419] shadow-[0_0_20px_rgba(var(--color-gold-rgb),.2)] disabled:cursor-not-allowed disabled:opacity-50 lg:hidden"
          >
            <Rocket className="h-4 w-4" />
            {connecting ? 'Connecting...' : 'Connect'}
          </button>

        </header>

        <section className="relative min-h-[360px] overflow-hidden px-4 pt-9 sm:min-h-[650px] sm:px-7 sm:pt-14 md:min-h-[700px] lg:min-h-[760px] lg:px-14 lg:pt-20 xl:px-20">
          <div className="pointer-events-none absolute inset-0 opacity-60 sm:opacity-70">
            <AppImage
              src={worldMap}
              alt=""
              fill
              priority
              sizes="100vw"
              className="object-cover object-[58%_center] sm:object-center"
              aria-hidden="true"
            />
          </div>
          <div className="absolute inset-0 opacity-60 [background-image:radial-gradient(circle_at_70%_30%,rgba(0,126,255,.18),transparent_35%),linear-gradient(rgba(10,60,110,.12)_1px,transparent_1px),linear-gradient(90deg,rgba(10,60,110,.12)_1px,transparent_1px)] [background-size:auto,38px_38px,38px_38px]" />

          <div
            className="hero-logo-scene pointer-events-none absolute"
            role="img"
            aria-label="Animated Aethera coin landing on a holographic pedestal"
          >
            <AppImage
              src={heroBase}
              alt=""
              fill
              priority
              sizes="(max-width: 639px) 430px, (max-width: 767px) 650px, (max-width: 1023px) 720px, (max-width: 1279px) 760px, 820px"
              className="hero-base-art object-contain opacity-95"
              aria-hidden="true"
            />
            <div className="hero-coin-bounce absolute left-[2%] top-[1.5%] h-[67%] w-[96%] will-change-transform">
              <AppImage
                src={heroCoin}
                alt=""
                fill
                priority
                sizes="(max-width: 639px) 225px, (max-width: 1023px) 540px, 680px"
                className="object-contain drop-shadow-[0_10px_16px_rgba(0,88,255,.42)]"
                aria-hidden="true"
              />
            </div>
            <span className="hero-coin-impact absolute bottom-[18.5%] left-1/2 h-[3.5%] w-[48%] -translate-x-1/2 rounded-[50%] bg-cyan-300/70 blur-md" />
          </div>

          <div className="relative z-10 w-[64%] min-w-0 max-w-[250px] sm:w-[58%] sm:max-w-[390px] md:w-[54%] md:max-w-[450px] lg:w-[48%] lg:max-w-[600px]">
            <p className="text-[14px]  font-bold uppercase tracking-[.035em] text-[#58b7e9] sm:text-[34px]">Community Powered</p>
            <p className="mt-1 text-[16px] font-bold uppercase tracking-[.02em] text-white sm:mt-2 sm:text-[40px]">Mining Ecosystem</p>
            {/* <h1 className="mt-3 text-[32px] font-black leading-[.98] tracking-[-.03em] text-white min-[390px]:text-[40px] sm:mt-4 sm:text-[58px] md:text-[66px] lg:text-[78px] xl:text-[90px]">AETHERA</h1> */}
            <p className="mt-3 whitespace-nowrap text-[10px] font-semibold tracking-[.01em] text-[var(--color-gold)] min-[390px]:text-[11px] sm:mt-4 sm:text-[18px] md:text-[20px] lg:text-[24px]">
              MINE <span className="px-0.5 text-[var(--color-gold)] sm:px-1">•</span> HOLD <span className="px-0.5 text-[var(--color-gold)] sm:px-1">•</span> GROW TOGETHER
            </p>
            <p className="mt-4 max-w-[200px] text-[9px] leading-[1.65] text-[#d3dce7] min-[390px]:text-[10px] sm:mt-5 sm:max-w-[315px] sm:text-[14px] md:text-[15px] lg:max-w-[490px] lg:text-[17px]">
              A decentralized ecosystem built on transparency, community participation, and sustainable growth powered by smart contracts.
            </p>

            <div className="mt-3 flex flex-nowrap gap-2 sm:mt-6 sm:gap-3">
              <button onClick={() => void launch()} disabled={connecting} className="flex h-9 shrink-0 items-center gap-1 rounded-lg bg-gradient-to-b from-[var(--color-gold)] to-[var(--color-gold)] px-2.5 text-[9px] font-bold text-[#101419] shadow-[0_0_20px_rgba(var(--color-gold-rgb),.2)] disabled:cursor-wait disabled:opacity-70 min-[390px]:h-10 min-[390px]:px-3 min-[390px]:text-[10px] sm:h-[54px] sm:gap-2 sm:rounded-xl sm:px-5 sm:text-[14px]">
                <Rocket className="h-3.5 w-3.5 sm:h-[17px] sm:w-[17px]" />
                {connecting ? 'Connecting...' : 'Connect'}
              </button>
              <button onClick={downloadPresentation} className="flex h-9 shrink-0 items-center gap-1 rounded-lg border border-[var(--color-gold)] bg-[#071226]/85 px-2.5 text-[9px] font-semibold text-white min-[390px]:h-10 min-[390px]:px-3 min-[390px]:text-[10px] sm:h-[54px] sm:gap-2 sm:rounded-xl sm:px-5 sm:text-[14px]">
                <FileDown className="h-3.5 w-3.5 sm:h-[18px] sm:w-[18px]" />
                Download PPT
              </button>
            </div>

          </div>
        </section>

        <section id="why-aethera" className="scroll-mt-24 px-4 pb-0 sm:px-7 lg:px-14 lg:pt-5 xl:px-20">
          <SectionTitle>Why Aethera?</SectionTitle>
          <div className="grid grid-cols-2 items-stretch gap-2.5 lg:grid-cols-4 lg:gap-4">
            <FeatureCard icon={Users} title="Community Powered" text="Built by the community, for the community." />
            <FeatureCard icon={ShieldCheck} title="Transparent & Secure" text="Smart contracts ensure transparency and complete security." />
            <FeatureCard icon={TrendingUp} title="Sustainable Growth" text="Balanced ecosystem designed for long-term sustainable growth." />
            <FeatureCard icon={Blocks} title="Decentralized Ecosystem" text="Decentralized protocol with fair and open participation." />
          </div>
          <div className="flex justify-center py-2 sm:py-3">
            <ChevronDown strokeWidth={1.2} className="h-10 w-10 text-[#0c7bd0] drop-shadow-[0_0_10px_#0878cb] sm:h-14 sm:w-14" />
          </div>
        </section>

        <PublicGallery galleryType="reward" />
        <PublicVideoGallery />
        <PublicGallery galleryType="community" />

        <section id="vision" className="index-tight-section scroll-mt-24 px-4 pb-8 sm:px-7 lg:px-14 xl:px-20">
          <SectionTitle>Our Vision</SectionTitle>
          <div className="index-feature-panel grid grid-cols-[72px_minmax(0,1fr)] items-center gap-4 rounded-2xl border border-[#1c3d60] bg-[radial-gradient(circle_at_12%_50%,rgba(0,112,255,.2),transparent_28%),#031024] p-4 sm:grid-cols-[110px_minmax(0,1fr)] sm:gap-6 sm:p-6 lg:grid-cols-[135px_minmax(0,1fr)] lg:p-7">
            <div className="relative h-[72px] w-[72px] sm:h-[110px] sm:w-[110px] lg:h-[135px] lg:w-[135px]">
              <div className="absolute inset-2 rounded-full border border-[#1f70ad] shadow-[0_0_26px_rgba(0,143,255,.3),inset_0_0_28px_rgba(0,143,255,.14)] sm:inset-3" />
              <AppImage src={coin} alt="Aethera vision emblem" fill className="object-contain drop-shadow-[0_0_25px_rgba(0,153,255,.55)]" />
            </div>
            <p className="text-left text-[10px] leading-[1.7] text-[#dde6ef] min-[390px]:text-[11px] sm:text-[16px] sm:leading-7 lg:text-[18px] lg:leading-8">
              To build one of the most trusted <span className="font-semibold text-[var(--color-gold)]">community-powered mining ecosystems</span> where transparency, decentralization, and sustainable rewards become the foundation of long-term growth.
            </p>
          </div>
        </section>

        <section id="tokenomics" className="scroll-mt-24 px-4 pb-8 sm:px-7 lg:px-14 xl:px-20">
          <SectionTitle>Tokenomics</SectionTitle>
          <div className="app-outer-panel overflow-hidden rounded-2xl border border-[#1a3b60] bg-[#031024]/90 px-4 sm:px-6">
            {[
              [Coins, 'Total Supply', '200,000 AETH'],
              [Link2, 'Blockchain', 'Binance Smart Chain (BSC)'],
              [CircleDollarSign, 'Initial Liquidity', '$2,000 USDT'],
              [Rocket, 'Launch Price', '$0.01 Per AETH'],
              [Users, 'Ecosystem', 'Community Powered'],
              [FileCheck2, 'Contract', 'Smart Contract Controlled'],
              [LockKeyhole, 'Liquidity', 'Cake LP Tokens Burned'],
              [BadgeCheck, 'Ownership', 'Renounced'],
            ].map(([Icon, key, value]) => {
              const RowIcon = Icon as IconType;
              return (
                <div key={String(key)} className="grid grid-cols-[28px_minmax(0,1fr)_minmax(0,1.35fr)] items-center gap-2 border-b border-[#15304a] py-3 last:border-b-0 sm:grid-cols-[36px_minmax(0,1fr)_minmax(0,1.5fr)] sm:gap-4 sm:py-4">
                  <RowIcon className="h-5 w-5 text-[var(--color-gold)] sm:h-6 sm:w-6" strokeWidth={1.7} />
                  <span className="text-[10px] font-bold uppercase tracking-wide text-[#9db2c8] sm:text-sm">{key as string}</span>
                  <span className="text-right text-[11px] font-bold leading-4 text-white sm:text-base">{value as string}</span>
                </div>
              );
            })}
          </div>
        </section>

        <section className="px-4 pb-8 sm:px-7 lg:px-14 xl:px-20">
          <SectionTitle>Special Features</SectionTitle>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4 lg:grid-cols-5 lg:gap-5">
            <FeatureIcon icon={Zap} title="Instant Affiliate Rewards" text="Real-time rewards distribution." />
            <FeatureIcon icon={FileCheck2} title="Smart Contract Automation" text="Transparent and automatic system." />
            <FeatureIcon icon={Eye} title="Transparent On-Chain" text="Everything is on-chain and verifiable." />
            <FeatureIcon icon={Users} title="Community Driven" text="Built by the community, for the community." />
          </div>
        </section>

        <section className="px-4 pb-0 sm:px-7 lg:px-14 xl:px-20">
          <div className="index-feature-panel rounded-2xl border border-[#1a3b60] bg-[#031024]/80 p-4 sm:p-5">
            <SectionTitle>Aethera Protocol Ingredients</SectionTitle>
            <div className="overflow-hidden rounded-xl border border-[#1a3b60] bg-[#031024]/95">
              <IngredientRow icon={Users} title="Creator Address" detail={creatorAddress} href={creatorBscScanUrl} />
              <IngredientRow icon={KeyRound} title="Published Private Key" detail={publishedPrivateKeyAddress} href={creatorBscScanUrl}  />
              <IngredientRow icon={BadgeCheck} title="AETH Address" detail={CONTRACTS.aethera} href={bscScanAddress(CONTRACTS.aethera)} />
              <IngredientRow icon={Settings} title="Mining Engine" detail={CONTRACTS.mining} href={bscScanAddress(CONTRACTS.mining)} />
              {/* <IngredientRow icon={Rabbit} title="Pancake V2 Router" detail={pancakeV2RouterAddress} href={bscScanAddress(pancakeV2RouterAddress)} /> */}
              {/* <IngredientRow icon={DollarSign} title="USDT Address" detail={CONTRACTS.usdt} href={bscScanAddress(CONTRACTS.usdt)} /> */}
            </div>
          </div>
          <div className="flex justify-center py-2 sm:py-3">
            <ChevronDown strokeWidth={1.2} className="h-10 w-10 text-[#0c7bd0] drop-shadow-[0_0_10px_#0878cb] sm:h-14 sm:w-14" />
          </div>
        </section>

        <section id="stake-participation" className="scroll-mt-24 px-4 pb-9 sm:px-7 lg:px-14 xl:px-20">
          <SectionTitle>Stake Participation</SectionTitle>
          <p className="-mt-2 mb-5 text-center text-[10px] text-[#b7c3cf] sm:text-[13px]">
            Your membership tier determines the maximum mining cap generated from Stake Participation.
          </p>
          <div className="mx-auto grid max-w-4xl grid-cols-2 gap-3 sm:gap-5">
            <RewardCard
              icon={Users}
              percent="200%"
              title="Standard User"
              text="Mining cap equal to 200% of eligible Stake Participation."
              tone="blue"
            />
            <RewardCard
              icon={BadgeCheck}
              percent="300%"
              title="Owner Club Member"
              text="Enhanced mining cap equal to 300% of eligible Stake Participation."
              tone="gold"
            />
          </div>
        </section>

        <section id="module" className="index-tight-section scroll-mt-24 px-4 pb-9 sm:px-7 lg:px-14 xl:px-20">
          <div className="mb-1 text-center">
            <p className="text-[13px] font-semibold uppercase tracking-[.38em] text-white sm:text-[22px]">Ecosystem</p>
            <h2 className="mt-1 text-[23px] font-black uppercase tracking-wide text-[var(--color-gold)] sm:text-[42px]">Working Module</h2>
            <p className="mt-1 text-[9px] text-[#c1ccd7] sm:text-[16px]">Simple. Transparent. Decentralized.</p>
          </div>

          <div className="ecosystem-module relative mt-5 grid grid-cols-1 items-stretch gap-5 sm:mt-7 sm:grid-cols-[.82fr_1.25fr_.82fr] sm:gap-5">
            <div className="module-side relative z-10 grid grid-cols-2 gap-3 sm:grid-cols-1 sm:gap-6">
              <div className="app-outer-panel relative rounded-xl border border-[#285784] bg-[#05162c]/95 p-2 text-center shadow-[0_0_22px_rgba(17,125,234,.12)] sm:rounded-2xl sm:p-5">
                <span className="absolute left-2 top-2 flex h-8 w-8 items-center justify-center rounded-full border-2 border-[#388fe0] bg-[#08204a] text-sm font-bold text-white sm:left-3 sm:top-3 sm:h-12 sm:w-12 sm:text-xl">1</span>
                <UserPlus className="mx-auto mt-2 h-10 w-10 text-[#38aaff] drop-shadow-[0_0_10px_#188ce9] sm:h-20 sm:w-20" />
                <h3 className="mt-2 text-[8px] font-bold uppercase text-[#43b7f4] sm:text-[16px]">Registration</h3>
                <p className="mt-2 text-[10px] leading-4 text-white sm:text-[12px] sm:leading-5">Free for Everyone<br/>Start your journey<br/>with AETHERA</p>
              </div>
              <div className="app-outer-panel relative rounded-xl border border-[#285784] bg-[#05162c]/95 p-2 text-center shadow-[0_0_22px_rgba(17,125,234,.12)] sm:rounded-2xl sm:p-5">
                <span className="absolute left-2 top-2 flex h-8 w-8 items-center justify-center rounded-full border-2 border-[#388fe0] bg-[#08204a] text-sm font-bold text-white sm:left-3 sm:top-3 sm:h-12 sm:w-12 sm:text-xl">2</span>
                <Coins className="mx-auto mt-2 h-10 w-10 text-[#38aaff] drop-shadow-[0_0_10px_#188ce9] sm:h-20 sm:w-20" />
                <h3 className="mt-2 text-[8px] font-bold uppercase text-[#43b7f4] sm:text-[16px]">Stake</h3>
                <p className="mt-2 text-[10px] leading-4 text-white sm:text-[12px] sm:leading-5">Contribute Daily<br/><strong className="text-[var(--color-gold)]">$10 - $1,000</strong><br/>Daily</p>
              </div>
            </div>

            <div className="relative z-10 flex items-center justify-center">
              <div className="flex aspect-square w-full max-w-[280px] flex-col items-center justify-center rounded-full border-2 border-[var(--color-gold)] bg-[radial-gradient(circle,#082548_0%,#031329_65%,#020b19_100%)] p-5 text-center shadow-[0_0_28px_rgba(0,145,255,.35),inset_0_0_25px_rgba(0,126,255,.15)] sm:max-w-[360px] sm:border-[3px] sm:p-8">
                <span className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-[var(--color-gold)] bg-[#07152a] text-sm font-bold text-[var(--color-gold)] sm:h-12 sm:w-12 sm:text-xl">3</span>
                <FileCheck2 className="mt-2 h-10 w-10 text-[#39aaff] drop-shadow-[0_0_12px_#168eea] sm:h-20 sm:w-20" />
                <h3 className="mt-1 text-[9px] font-bold uppercase text-[var(--color-gold)] sm:text-[18px]">Distribution</h3>
                <p className="text-[10px] text-white sm:text-[12px]">Contribution Distribution</p>
                <div className="mt-3 text-left text-[10px] leading-5 sm:text-[12px]">
                  <p><strong className="text-[#43d3a0]">60%-85%</strong> <span className="text-white">To PancakeSwap (DEX)</span></p>
                  <p><strong className="text-[#44aaff]">15%-35%</strong> <span className="text-white">To Mining Engine</span></p>
                  <p><strong className="text-[#a66aff]">5%</strong> <span className="text-white">Project Dev. Fee</span></p>
                </div>
              </div>
            </div>

            <div className="module-side relative z-10 grid grid-cols-2 gap-3 sm:grid-cols-1 sm:gap-6">
              <div className="app-outer-panel relative rounded-xl border border-[#285784] bg-[#05162c]/95 p-2 text-center shadow-[0_0_22px_rgba(17,125,234,.12)] sm:rounded-2xl sm:p-5">
                <span className="absolute left-2 top-2 flex h-8 w-8 items-center justify-center rounded-full border-2 border-[#388fe0] bg-[#08204a] text-sm font-bold text-white sm:left-3 sm:top-3 sm:h-12 sm:w-12 sm:text-xl">4</span>
                <Pickaxe className="mx-auto mt-2 h-10 w-10 text-[#38aaff] drop-shadow-[0_0_10px_#188ce9] sm:h-20 sm:w-20" />
                <h3 className="mt-2 text-[8px] font-bold uppercase text-[#43b7f4] sm:text-[16px]">Mining</h3>
                <p className="mt-2 text-[10px] leading-4 text-white sm:text-[12px] sm:leading-5">AETH Mining Starts<br/><strong className="text-[var(--color-gold)]">Min. $1</strong></p>
              </div>
              <div className="app-outer-panel relative rounded-xl border border-[#285784] bg-[#05162c]/95 p-2 text-center shadow-[0_0_22px_rgba(17,125,234,.12)] sm:rounded-2xl sm:p-5">
                <span className="absolute left-2 top-2 flex h-8 w-8 items-center justify-center rounded-full border-2 border-[#388fe0] bg-[#08204a] text-sm font-bold text-white sm:left-3 sm:top-3 sm:h-12 sm:w-12 sm:text-xl">5</span>
                <Gift className="mx-auto mt-2 h-10 w-10 text-[var(--color-gold)] drop-shadow-[0_0_10px_var(--color-gold)] sm:h-20 sm:w-20" />
                <h3 className="mt-2 text-[7px] font-bold uppercase leading-3 text-[#43b7f4] sm:text-[14px] sm:leading-5">Reward Distribution</h3>
                <p className="mt-2 text-[10px] leading-4 text-white sm:text-[12px] sm:leading-5">Instant, Daily,<br/>Weekly &amp; Monthly<br/>Rewards</p>
              </div>
            </div>

            <svg
              className="pointer-events-none absolute inset-0 z-0 hidden h-full w-full overflow-visible sm:block"
              viewBox="0 0 1000 540"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <defs>
                <filter id="moduleArrowGlow" x="-40%" y="-40%" width="180%" height="180%">
                  <feGaussianBlur stdDeviation="5" result="blur" />
                  <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                </filter>
                <marker id="moduleArrowHead" markerWidth="10" markerHeight="10" refX="8" refY="5" orient="auto" markerUnits="strokeWidth">
                  <path d="M0,0 L10,5 L0,10 L3,5 Z" fill="#48c8ff" />
                </marker>
                <linearGradient id="moduleArrowLeft" x1="1" y1="0" x2="0" y2="0">
                  <stop offset="0" stopColor="#1d8ee8" stopOpacity=".45" />
                  <stop offset="1" stopColor="#53d2ff" />
                </linearGradient>
                <linearGradient id="moduleArrowRight" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0" stopColor="#1d8ee8" stopOpacity=".45" />
                  <stop offset="1" stopColor="#53d2ff" />
                </linearGradient>
              </defs>

              <g fill="none" strokeWidth="3" filter="url(#moduleArrowGlow)" markerEnd="url(#moduleArrowHead)">
                <path d="M420 155 C365 155 365 86 278 86" stroke="url(#moduleArrowLeft)" />
                <path d="M420 385 C365 385 365 454 278 454" stroke="url(#moduleArrowLeft)" />
                <path d="M580 155 C635 155 635 86 722 86" stroke="url(#moduleArrowRight)" />
                <path d="M580 385 C635 385 635 454 722 454" stroke="url(#moduleArrowRight)" />
              </g>

              <g fill="#071a31" stroke="#42caff" strokeWidth="4" filter="url(#moduleArrowGlow)">
                <circle cx="420" cy="155" r="9" />
                <circle cx="420" cy="385" r="9" />
                <circle cx="580" cy="155" r="9" />
                <circle cx="580" cy="385" r="9" />
              </g>
              <g fill="#8ce7ff">
                <circle cx="420" cy="155" r="3" />
                <circle cx="420" cy="385" r="3" />
                <circle cx="580" cy="155" r="3" />
                <circle cx="580" cy="385" r="3" />
              </g>
            </svg>
          </div>
        </section>

        <section id="rewards" className="scroll-mt-24 px-4 pb-10 sm:px-7 lg:px-14 xl:px-20">
          <SectionTitle>Reward Distribution</SectionTitle>
          <p className="-mt-2 mb-5 text-center text-[10px] text-[#b7c3cf] sm:text-[13px]">40% of every contribution goes to Protocol Engine for community rewards.</p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <RewardCard icon={Users} percent="7%" title="Direct Spot Reward" text="Instant reward for direct referrals." tone="blue" />
            <RewardCard icon={Layers3} percent="18%" title="Multi-Level Reward" text="Level 2 to Level 15 deep rewards." tone="purple" />
            <RewardCard icon={CalendarDays} percent="5%" title="Daily AETHERA Pool" text="Daily pool reward for all qualifiers." tone="gold" />
            <RewardCard icon={Trophy} percent="5%" title="Weekly Champion Pool" text="Weekly pool rewards for top performers." tone="green" />
          </div>
          <div className="mt-3 flex items-start gap-3 overflow-hidden rounded-xl border border-[#1d4268] bg-[linear-gradient(90deg,#06162b_0%,#06162b_72%,#092449_100%)] p-4 text-[9px] leading-4 text-[#c6d2de] sm:mt-4 sm:gap-4 sm:rounded-2xl sm:text-[12px] sm:leading-5">
            <ul className="min-w-0 flex-1 space-y-2" aria-label="Reward conditions">
              {[
                'Direct and level rewards apply to contributions of $50 and above.',
                'Rewards reduce users Mining Cap. If no cap is available, no reward is paid.',
                'Appreciation benifits also reduce users Mining Cap. If no cap is available, no reward is paid.',
                'Daily and Weekly Pool rewards do not reduce users Mining Cap.',
              ].map((item) => (
                <li key={item} className="flex items-start gap-2">
                  <span className="mt-[0.4em] h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--color-gold)]" aria-hidden="true" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="px-4 pb-10 sm:px-7 lg:px-14 xl:px-20">
          <SectionTitle>Trusted • Transparent • Secure</SectionTitle>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-5">
            <FeatureIcon icon={ShieldCheck} title="Smart Contract Audited" text="Audited and secured by third-party experts." />
            <FeatureIcon icon={LockKeyhole} title="Ownership Renounced" text="No admin control. Community driven." />
            <FeatureIcon icon={Eye} title="100% Transparency" text="All transactions are on-chain and verifiable." />
            <FeatureIcon icon={Layers3} title="Built on BSC" text="Powered by Binance Smart Chain." />
          </div>
        </section>

        <section className="px-4 pb-10 sm:px-7 lg:px-14 xl:px-20">
          <div className="index-cta-panel grid grid-cols-1 items-center gap-3 rounded-2xl border border-[#244869] bg-[radial-gradient(circle_at_20%_50%,rgba(0,106,255,.18),transparent_35%),#061428] p-5 text-center sm:grid-cols-[220px_1fr] sm:gap-5 sm:p-7 sm:text-left">
            <div className="relative mx-auto h-[96px] w-[96px] sm:h-[150px] sm:w-[150px]">
              <span className="absolute inset-[10%] rounded-full bg-[#087fd4]/20 blur-xl" aria-hidden="true" />
              <AppImage src={coin} alt="Aethera app logo" fill className="object-contain drop-shadow-[0_0_20px_rgba(var(--color-gold-rgb),.4)]" />
            </div>
            <div>
              <h2 className="text-[22px] font-extrabold uppercase leading-tight text-white sm:text-[34px]">
                Ready to start your <span className="text-[var(--color-gold)]">mining journey?</span>
              </h2>
              <p className="mt-3 text-[11px] leading-5 text-[#c9d4df] sm:text-[14px]">
                Be part of the future.<br />
                Mine • Hold • Grow Together with <span className="text-[var(--color-gold)]">AETHERA</span>.
              </p>
              <div className="mt-4 flex justify-center gap-2 sm:mt-5 sm:justify-start sm:gap-3">
                <button onClick={() => void launch()} disabled={connecting} className="flex h-10 items-center gap-1.5 rounded-xl bg-gradient-to-b from-[var(--color-gold)] to-[var(--color-gold)] px-4 text-[10px] font-bold text-[#101419] disabled:cursor-wait disabled:opacity-70 sm:h-11 sm:gap-2 sm:px-5 sm:text-[12px]">
                  <Rocket className="h-3 w-3 sm:h-4 sm:w-4" />
                  {connecting ? 'Connecting...' : 'Connect'}
                </button>
                <button onClick={downloadPresentation} className="flex h-10 items-center gap-1.5 rounded-xl border border-[var(--color-gold)] bg-[#071226]/85 px-4 text-[10px] font-semibold text-white sm:h-11 sm:gap-2 sm:px-5 sm:text-[12px]">
                  <FileDown className="h-3 w-3 sm:h-4 sm:w-4" />
                  Download PPT
                </button>
              </div>
            </div>
          </div>
        </section>

        <footer className="border-t border-[#0d2845] bg-[#020b18] px-5 pb-7 pt-9 sm:px-8 sm:py-10 lg:px-14 xl:px-20">
          <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-7 sm:flex-row sm:items-start">
            {/* <div className="flex items-start gap-3">
              <AppImage src={coin} alt="Aethera" width={64} height={64} className="h-9 w-9 object-contain sm:h-14 sm:w-14" />
              <div>
                <p className="text-[11px] font-extrabold text-white sm:text-[22px]">AETHERA</p>
                <p className="text-[5.5px] leading-3 text-[#b8c4d0] sm:text-[10px]">Community Powered<br/><span className="text-[var(--color-gold)]">Mining Ecosystem</span></p>
              </div>
            </div> */}

            {/* <div>
              <p className="text-[10px] font-bold uppercase text-[#44a7df]">Quick Links</p>
              <div className="mt-3 grid gap-2 text-[9px] text-[#b1bdca] sm:text-[10px]">
                <a href="#">Home</a><a href="#module">Ecosystem</a><a href="#tokenomics">Tokenomics</a><a href="#rewards">Rewards</a><a href="#">Whitepaper</a>
              </div>
            </div> */}

            {/* <div>
              <p className="text-[10px] font-bold uppercase text-[#44a7df]">Useful Links</p>
              <div className="mt-3 grid gap-2 text-[9px] text-[#b1bdca] sm:text-[10px]">
                <a href="#">Audit</a><a href="#">Docs</a><a href="#">FAQ</a><a href="#">Roadmap</a><a href="#">Privacy Policy</a>
              </div>
            </div> */}

            <div className="w-full text-center sm:w-auto sm:text-left">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#44a7df]">Join Community</p>
              <div className="mt-3 grid gap-3 text-[#b1bdca]">
                <a href="https://t.me/aetheradefi" target="_blank" rel="noopener noreferrer" className="mx-auto flex h-10 w-fit min-w-[180px] items-center justify-center gap-2 rounded-lg border border-[#39a9ff]/55 bg-gradient-to-r from-[#087bc1] to-[#0ca7d8] px-5 text-sm font-extrabold text-white shadow-[0_10px_30px_rgba(8,155,211,.25)] transition hover:-translate-y-0.5 hover:brightness-110"><Send className="h-5 w-5"/>Join Telegram</a>
                {/* <a href="#" className="flex items-center gap-2"><span className="inline-flex h-4 w-4 items-center justify-center text-xs font-bold" aria-hidden="true">𝕏</span>Twitter (X)</a> */}
                {/* <a href="#" className="flex items-center gap-2"><MessageCircle className="h-4 w-4 text-[#7787ff]"/>Discord</a> */}
                {/* <a href="#" className="flex items-center gap-2"><span className="inline-flex h-4 w-4 items-center justify-center rounded bg-[#ff4242] text-[8px] text-white" aria-hidden="true">▶</span>YouTube</a> */}
              </div>
            </div>
          </div>

          <div className="mx-auto mt-8 flex max-w-6xl flex-col items-center gap-2 border-t border-[#0d2845] pt-5 text-center text-[9px] text-[#8f9cac] sm:flex-row sm:justify-between sm:text-left">
            <p>© 2026 AETHERA. All Rights Reserved.</p>
            <p>Built by the community. Powered by smart contracts.</p>
          </div>
        </footer>
      </div>
      <button
        type="button"
        onClick={scrollToTop}
        aria-label="Scroll to top"
        className={`fixed bottom-5 right-4 z-50 flex h-11 w-11 items-center justify-center rounded-full border border-[var(--color-gold)] bg-[#06152a]/95 text-[var(--color-gold)] shadow-[0_0_22px_rgba(var(--color-gold-rgb),.28)] backdrop-blur-md transition-all duration-200 hover:-translate-y-1 hover:bg-[#0b1f3d] sm:bottom-7 sm:right-7 sm:h-12 sm:w-12 ${showScrollTop ? 'pointer-events-auto translate-y-0 opacity-100' : 'pointer-events-none translate-y-3 opacity-0'}`}
      >
        <ArrowUp className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={2.2} />
      </button>
      <Modal open={showSponsorRequired} onClose={() => setShowSponsorRequired(false)} title="Sponsor Required" size="sm">
        <div className="space-y-4 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-primary/40 bg-primary/10 text-primary">
            <Users className="h-7 w-7" />
          </div>
          <div>
            <p className="text-sm font-bold text-foreground">This wallet is not registered with AETHERA.</p>
            <p className="mt-2 text-xs leading-5 text-muted-foreground">New users must open a valid referral link provided by their sponsor. The link includes the sponsor wallet and placement side required for registration.</p>
          </div>
          <button type="button" onClick={() => setShowSponsorRequired(false)} className="btn-gold flex h-11 w-full items-center justify-center rounded-xl text-sm font-bold">
            I Understand
          </button>
        </div>
      </Modal>
    </main>
  );
}
